import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  TARGET_SYSTEM_AUTH_COMPATIBILITY,
  AUTH_METHOD_FIELDS,
  AUTH_FIELD_DEFAULTS,
  DEFAULT_CUSTOM_SCHEDULE
} from '../constants';
import {
  initializeFormData,
  isConnectionField,
  generateScheduleLabel,
  buildSubmitParameters,
  validateForm
} from '../utils';

/**
 * Removes old auth-specific fields from state
 */
const removeAuthFields = (state, authMethod) => {
  const fieldsToRemove = AUTH_METHOD_FIELDS[authMethod] || [];
  const cleanedState = { ...state };

  fieldsToRemove.forEach((field) => {
    delete cleanedState[field];
  });

  return cleanedState;
};

/**
 * Handles target system change and returns new state
 */
const handleTargetSystemChange = (prevState, newTargetSystem) => {
  const compatibleAuthMethods =
    TARGET_SYSTEM_AUTH_COMPATIBILITY[newTargetSystem] || [];
  const isCurrentAuthCompatible = compatibleAuthMethods.includes(
    prevState.authMethod
  );
  const newAuthMethod = isCurrentAuthCompatible ? prevState.authMethod : '';

  // If auth method stays the same, just update target system
  if (newAuthMethod === prevState.authMethod) {
    return {
      ...prevState,
      targetSystem: newTargetSystem,
      authMethod: newAuthMethod
    };
  }

  // Auth method is being reset - clean up old fields and add new defaults
  const cleanedState = removeAuthFields(prevState, prevState.authMethod);
  const newAuthFields = AUTH_FIELD_DEFAULTS[newAuthMethod] || {};

  return {
    ...cleanedState,
    targetSystem: newTargetSystem,
    authMethod: newAuthMethod,
    ...newAuthFields
  };
};

/**
 * Handles auth method change and returns new state
 */
const handleAuthMethodChange = (prevState, newAuthMethod) => {
  const cleanedState = removeAuthFields(prevState, prevState.authMethod);
  const newAuthFields = AUTH_FIELD_DEFAULTS[newAuthMethod] || {};

  return {
    ...cleanedState,
    authMethod: newAuthMethod,
    ...newAuthFields
  };
};

/**
 * Builds log-safe parameters object (masks secrets)
 */
const buildLogParameters = (formData) => {
  const baseParams = {
    target: formData.targetSystem,
    method: formData.authMethod
  };

  if (formData.authMethod === 'iam-access-key') {
    return {
      ...baseParams,
      accessKeyId: formData.accessKeyId,
      secretAccessKey: formData.secretAccessKey ? '***' : '',
      awsRegion: formData.awsRegion
    };
  }

  if (formData.authMethod === 'oauth') {
    return {
      ...baseParams,
      clientId: formData.clientId,
      clientSecret: formData.clientSecret ? '***' : '',
      authorizationServerURL: formData.authorizationServerURL,
      tokenEndpointURL: formData.tokenEndpointURL,
      redirectURI: formData.redirectURI,
      scope: formData.scope
    };
  }

  return baseParams;
};

/**
 * Blurs the run-frequency select element after modal closes
 */
const blurRunFrequencySelect = () => {
  setTimeout(() => {
    const selectElement = document.getElementById('run-frequency');

    if (selectElement) {
      selectElement.blur();
    }
  }, 0);
};

/**
 * Processes custom schedule field change
 */
const processCustomScheduleChange = (prevSchedule, field, value) => {
  const newSchedule = {
    ...prevSchedule,
    [field]: value
  };

  // Reset fields when switching to months
  if (field === 'intervalUnit' && value === 'months') {
    newSchedule.intervalNumber = 1;
    newSchedule.repeatDays = [];
  }

  // Clear repeat days when switching away from weeks
  const isSwitchingFromWeeks =
    field === 'intervalUnit' &&
    value !== 'weeks' &&
    prevSchedule.intervalUnit === 'weeks';

  if (isSwitchingFromWeeks) {
    newSchedule.repeatDays = [];
  }

  return newSchedule;
};

/**
 * Builds the final scanner request object for submission
 */
const buildScannerRequest = (formData, mode, initialData) => {
  const parameters = buildSubmitParameters(formData);

  const baseRequest = {
    ...(formData.id && mode === 'edit' ? { id: formData.id } : {}),
    name: formData.name,
    description: formData.description,
    parameters,
    runPolicy: {
      runFrequency: formData.runFrequency,
      timeout: formData.timeout,
      maxRetryAttempts: formData.maxRetryAttempts
    }
  };

  // For edit mode, merge with initial data
  if (mode === 'edit' && initialData) {
    return {
      ...initialData,
      ...baseRequest,
      parameters: {
        ...(initialData.parameters || {}),
        ...parameters
      },
      runPolicy: {
        ...(initialData.runPolicy || {}),
        ...baseRequest.runPolicy
      }
    };
  }

  return baseRequest;
};

/**
 * Logs scanner request to console
 */
const logScannerRequest = (request, mode) => {
  // eslint-disable-next-line no-console
  console.log(`=== Scanner Configuration Request (${mode.toUpperCase()}) ===`);
  try {
    // eslint-disable-next-line no-console
    console.log(JSON.stringify(request, null, 2));
  } catch {
    // eslint-disable-next-line no-console
    console.log('Scanner Request (object):', request);
  }
  // eslint-disable-next-line no-console
  console.log('=====================================\n');
};

/**
 * Custom hook for managing scanner configuration form state and handlers
 *
 * @param {Object} initialData - Initial form data from props
 * @param {string} mode - Form mode ('add' or 'edit')
 * @param {Function} onSubmit - Submit callback
 * @returns {Object} Form state and handlers
 */
const useScannerForm = (initialData, mode, onSubmit) => {
  // Form data state
  const [formData, setFormData] = useState(() =>
    initializeFormData(initialData)
  );

  // UI state
  const [connectionStatus, setConnectionStatus] = useState('idle');
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [customSchedule, setCustomSchedule] = useState(DEFAULT_CUSTOM_SCHEDULE);

  // Development helper: Log form data changes
  useEffect(() => {
    const parameters = buildLogParameters(formData);
    const scannerData = {
      name: formData.name,
      description: formData.description,
      parameters,
      runPolicy: {
        runFrequency: formData.runFrequency,
        timeout: formData.timeout,
        maxRetryAttempts: formData.maxRetryAttempts
      },
      connectionStatus
    };

    // eslint-disable-next-line no-console
    console.log('📝 Scanner Data Updated:', scannerData);
  }, [formData, connectionStatus]);

  // Reset connection status when connection fields change
  const resetConnectionStatusIfNeeded = useCallback(
    (field) => {
      setFormData((prev) => {
        const shouldReset =
          isConnectionField(field, prev.authMethod) &&
          connectionStatus !== 'idle';

        if (shouldReset) {
          setConnectionStatus('idle');
        }

        return prev;
      });
    },
    [connectionStatus]
  );

  // Generic change handler for all form fields
  const handleChange = useCallback(
    (field, value) => {
      // Handle target system change
      if (field === 'targetSystem') {
        setFormData((prev) => handleTargetSystemChange(prev, value));
        resetConnectionStatusIfNeeded(field);

        return;
      }

      // Handle auth method change
      if (field === 'authMethod') {
        setFormData((prev) => handleAuthMethodChange(prev, value));
        resetConnectionStatusIfNeeded(field);

        return;
      }

      // Handle custom run frequency
      if (field === 'runFrequency' && value === 'Custom') {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setShowCustomModal(true);
        resetConnectionStatusIfNeeded(field);

        return;
      }

      // Handle all other fields
      setFormData((prev) => ({ ...prev, [field]: value }));
      resetConnectionStatusIfNeeded(field);
    },
    [resetConnectionStatusIfNeeded]
  );

  // Handle custom schedule modal save
  const handleCustomScheduleSave = useCallback(() => {
    const scheduleLabel = generateScheduleLabel(customSchedule);

    setFormData((prev) => ({ ...prev, runFrequency: scheduleLabel }));
    setShowCustomModal(false);
    blurRunFrequencySelect();
  }, [customSchedule]);

  // Handle custom schedule modal cancel
  const handleCustomScheduleCancel = useCallback(() => {
    setFormData((prev) => ({ ...prev, runFrequency: 'Daily' }));
    setShowCustomModal(false);
    blurRunFrequencySelect();
  }, []);

  // Handle custom schedule field changes
  const handleCustomScheduleChange = useCallback((field, value) => {
    setCustomSchedule((prev) =>
      processCustomScheduleChange(prev, field, value)
    );
  }, []);

  // Toggle advanced settings accordion
  const toggleAdvancedSettings = useCallback(() => {
    setShowAdvancedSettings((prev) => !prev);
  }, []);

  // Toggle email notifications
  const handleEmailNotificationsToggle = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      emailNotificationsEnabled: !prev.emailNotificationsEnabled
    }));
  }, []);

  // Add email to recipient list
  const handleAddEmail = useCallback((email) => {
    setFormData((prev) => ({
      ...prev,
      recipientEmails: [...prev.recipientEmails, email]
    }));
  }, []);

  // Remove email from recipient list
  const handleRemoveEmail = useCallback((emailToRemove) => {
    setFormData((prev) => ({
      ...prev,
      recipientEmails: prev.recipientEmails.filter(
        (email) => email !== emailToRemove
      )
    }));
  }, []);

  // Handle connection test
  const handleTestConnection = useCallback(() => {
    setConnectionStatus('checking');

    setTimeout(() => {
      setConnectionStatus('success');
    }, 2000);
  }, []);

  // Form submission handler
  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();

      const finalRequest = buildScannerRequest(formData, mode, initialData);

      logScannerRequest(finalRequest, mode);

      if (onSubmit) {
        const cleanRequest = JSON.parse(JSON.stringify(finalRequest));

        onSubmit(cleanRequest);
      }
    },
    [formData, mode, initialData, onSubmit]
  );

  // Form validation
  const isFormValid = useMemo(() => validateForm(formData), [formData]);

  return {
    // State
    formData,
    connectionStatus,
    showCustomModal,
    showAdvancedSettings,
    customSchedule,
    isFormValid,

    // Handlers
    handleChange,
    handleSubmit,
    handleTestConnection,
    handleCustomScheduleSave,
    handleCustomScheduleCancel,
    handleCustomScheduleChange,
    toggleAdvancedSettings,
    handleEmailNotificationsToggle,
    handleAddEmail,
    handleRemoveEmail
  };
};

export default useScannerForm;
